import { HELP_LINKS } from '../shared.constants';

interface ApiErrorDef {
  code: number;
  message?: string;
}

// Single source of truth for error codes; `ERROR_CODES` and `ErrorMessages` are derived from it.
// Entries with no `message` carry a per-call message (or none) and are absent from `ErrorMessages`.
const ERROR_DEFS = {
  CROSS_REGION_SHARING_UNSUPPORTED: {
    code: 303,
    message: `Cross-region sharing is not supported. <a target='_blank' href="${HELP_LINKS.SHARE_INVITE_REGION}">Learn more</a>.`,
  },
  MISSING_TRANSLATIONS: { code: 607, message: 'Rename translations required; retry with provided translations.' },
  GENERIC_ERROR: { code: 1000, message: 'An error occurred' }, // runtime error
  UPLOAD_CANCELED: { code: 1001, message: 'Upload canceled' },
  INTERNAL_SHARD_ERROR: { code: 1100, message: 'An internal error occurred.' }, // this is a shard error, invalid shard
  PUBLIC_LINK_URL_INVALID: { code: 1101, message: 'The public link URL is incorrect or invalid' },
  SHARE_INVITE_INVALID: { code: 1102, message: 'The share invitation is invalid' },
  REWARD_CODE_INVALID: { code: 1103, message: 'Invalid reward code given' },
  CONFIRMATION_KEY_INVALID: { code: 1104, message: 'Invalid confirmation key' },

  TWO_FACTOR_ENABLE_FAILED: { code: 1200 }, // always constructed with a custom message
  PASSWORD_HINT_INVALID_INPUT: { code: 1201 }, // always constructed with a custom message

  LINK_LOAD_FAILED: { code: 1202, message: 'Unable to load the link. Please try again later.' },

  FILE_LISTING_ERROR: { code: 1500, message: 'The file listing encountered an error.' },
  FILE_RENAME_ERROR: { code: 1501, message: 'Renaming the file encountered an error.' },
  FOLDER_RENAME_ERROR: { code: 1502, message: 'Renaming the folder encountered an error.' },
  FOLDER_NOT_FOUND: {
    code: 1503,
    message: 'Folder not found, please make sure you are accessing the correct link.',
  },

  INVITE_NOT_FOUND: { code: 1600, message: 'This invite cannot be found' },
  INVITE_NOT_FOR_YOU: { code: 1601, message: 'This invite was not for you.  Please log in as the invited user' },
  INVITE_MEMBER_NOT_FOUND: { code: 1602, message: 'Unable to locate the invited member' },
  INVITE_INVALID_STATE: { code: 1603, message: 'This invite has an invalid state.' },
  SHARE_INVALID: { code: 1604, message: 'This is an invalid share' },
  SHARE_INVITE_MIN_ONE_PERSON: { code: 1605, message: 'You must invite at least 1 person' },
  SHARE_ERROR: { code: 1606, message: 'There was an error with this share' },
  SHARE_FOLDER_NAME_DECRYPT_FAILED: { code: 1607, message: 'Could not decrypt the share folder name' },
  SHARE_FOLDER_NAME_DECRYPT_FAILED_ALT: { code: 1608, message: 'Could not decrypt the share folder name' },
  SHARE_VERIFICATION_FAILED: { code: 1609, message: 'Share verification did not return successfully' },
  SHARE_NESTED_NOT_SUPPORTED: {
    code: 1610,
    message: 'You are already sharing a folder inside.  Nested share folders are not supported.',
  },
  SHARE_INVITE_PROCESS_FAILED: { code: 1611, message: 'Could not process the share invitation' },
  SHARE_ACCEPT_ERROR: { code: 1612, message: 'An error occurred accepting this share' },
  SHARE_DECLINE_ERROR: { code: 1613, message: 'An error occurred declining the share' },
  SHARE_ACCEPT_LINKSHAREKEY_DECRYPT_FAILED: { code: 1614, message: 'An error occurred accepting this share' }, // linksharekey decrypt failed
  SHARE_PASSWORD_INCORRECT: { code: 1620, message: 'The share password was incorrect' },
  SHARE_PASSWORD_REQUIRED: { code: 1621, message: 'Please provide the share password' },
  SHARE_INVITE_SELF: { code: 1650, message: 'You cannot invite yourself to your own share' },
  SHARE_INVITE_ALREADY_MEMBER: { code: 1651, message: 'This person is already on the share' },
  SHARE_INVITE_BATCH_LIMIT: {
    code: 1652,
    message:
      'You can add up to 20 users at a time. Click the button below to add / send invites to these users now, and then continue to add more users as needed.',
  },
  SHARE_INVITE_ALREADY_QUEUED: { code: 1653, message: 'This person is alerday queued to be invited' },
  SHARE_INVITE_DISPLAY_NAME_REQUIRED: { code: 1654, message: 'You must enter a display name' },
  SHARE_INVITE_EMAIL_QUEUE_MAX: { code: 1655, message: 'A maximum of 5 emails may be queued.' },
  SHARE_INVITE_NO_EMAILS_QUEUED: { code: 1656, message: 'No email addresses were queued' },
  BILLING_DOCUMENT_EMAIL_FAILED: {
    code: 1657,
    message: 'An error occurred attempting to send billing documents via email.  Please try again later',
  },
  BILLING_DOCUMENT_NOT_FOUND: { code: 1658, message: 'Unable to locate the billing document' },
  DISPLAY_NAME_CONTAINS_URL: { code: 1659, message: 'Display name cannot contain URLs' },
  SHARE_ENCRYPTION_FAILED: { code: 1660, message: 'Share encryption failed. Please try again.' },

  SHARE_FILE_LIMIT: { code: 1666, message: 'New shares cannot exceed 30000 files.' }, // When user tries to share folder which contains 30000

  ACCOUNT_PROCESSING_CHANGES: {
    code: 1680,
    message:
      'Your Sync Account is currently processing changes to folders and files. Please leave your browser open and try again in a few minutes.',
  },
  DB_INTEGRITY_ERROR: {
    code: 1681,
    message: `Internal server error. Visit <a href='${HELP_LINKS.SUPPORT}' target='_blank'>${HELP_LINKS.SUPPORT}</a>`,
  }, // DB Integrity error

  LINK_DELETED_BY_OWNER: { code: 1700, message: 'This link has been deleted by the owner.' },
  ROLE_NAME_INVALID_CHARACTERS: { code: 1701, message: 'Role name can not contain special characters.' },

  // invite related
  INVITE_MISSING_ID: { code: 1702, message: 'The url is missing the invite id' },

  // Pathlist/favourite errors
  STARRED_ACCESS_ERROR: { code: 1800, message: 'Unable to access your starred right now. Please retry later.' },
  STAR_ADD_FAILED: { code: 1801 }, // always thrown with a custom message
  STAR_REMOVE_FAILED: { code: 1802 }, // always thrown with a custom message
  CWD_NOT_FOUND: { code: 1803, message: 'No current working directory found' },
  FILE_NOT_FOUND: { code: 1804, message: 'This file was not found' },

  // APP Rating Errors
  RATING_INPUT_INVALID: { code: 1900, message: 'Invalid rating input' },

  // Privacy center errors
  EXTENDED_PREVIEWS_NOT_ALLOWED: { code: 1950, message: 'Extended document previews is not allowed' },
  MICROSOFT_EDITING_NOT_ALLOWED: { code: 1951, message: 'Microsoft document editing is not allowed' },
  DOWNLOAD_ACCELERATOR_NOT_ALLOWED: { code: 1952, message: 'Download accelerator is not allowed' },
  MEDIA_STREAMING_NOT_ALLOWED: { code: 1953, message: 'Media streaming is not allowed' },
  EXTENDED_WEB_THUMBNAILS_NOT_ALLOWED: { code: 1954, message: 'Extended web thumbnails is not allowed' },

  // Encryption/decryption errors
  SHARE_KEY_MISSING: { code: 2000, message: 'Missing or invalid share key' },
  SHARE_KEY_RETRIEVAL_FAILED: { code: 2001 }, // thrown with no message
  PUBLIC_KEY_MISSING: { code: 2005, message: 'Missing or invalid public key' },
  PRIVATE_KEY_MISSING: { code: 2006, message: 'Missing or invalid private key' },
  LINK_PASSWORD_INVALID_PREFIX: { code: 2007 }, // thrown with no message
  FILE_NAME_DECRYPT_FAILED: { code: 2009, message: 'Unable to decrypt file name' },
  DATA_KEY_MISSING: { code: 2010, message: 'Missing or invalid data key' },
  DATA_KEY_DECRYPT_FAILED: { code: 2011 }, // thrown with no message
  DATA_KEY_ENCRYPT_FAILED: { code: 2012 }, // thrown with no message
  LINK_KEY_MISSING: { code: 2015, message: 'Missing or invalid link key' },
  DATA_CORRUPT_AUTH_HEADER: { code: 2020, message: 'Encrypted data may be corrupt, incorrect authentication header' },
  FILE_DECRYPT_FAILED: { code: 2021, message: 'Encrypted file could not be decrypted.' },
  FILE_NAME_ENCRYPT_FAILED: { code: 2022, message: 'Unable to encrypt the file name' },
  FILE_NAME_DECRYPT_FAILED_ALT: { code: 2023, message: 'Unable to decrypt the file name' },
  DATA_INVALID: { code: 2024, message: 'Encrypted data is invalid.' },
  FILE_DATA_DECRYPT_FAILED: { code: 2050, message: 'Error decrypting file data' },
  LINK_OR_SHARE_PASSWORD_DECRYPT_FAILED: { code: 2100, message: 'Error decrypting the link or share password' },
  LINK_KEY_INCORRECT: { code: 2110, message: 'The link key is incorrect' },
  SHARE_WRAP_KEY_INCORRECT: { code: 2115, message: 'The share wrap key is incorrect' },
  LINK_SHARE_KEYS_ENCRYPT_FAILED: { code: 2120, message: 'Error encrypting link share keys' },
  LINK_SHARE_KEYS_DECRYPT_FAILED: { code: 2121, message: 'Error decrypting link share keys' },
  SHARE_KEY_ENCRYPT_FAILED: { code: 2122, message: 'Error encrypting a share key' },
  ENCRYPTED_PASSWORD_INVALID: { code: 2123, message: 'The encrypted password is invalid or incorrect' },
  SHARE_KEY_PROCESSING_FAILED: { code: 2124 }, // thrown with no message
  LINK_SHARE_KEY_REENCRYPT_FAILED: { code: 2125 }, // thrown with no message
  TOKEN_ENCRYPT_FAILED: { code: 2130 }, // thrown with no message
  CWD_NOT_FOUND_ALT: { code: 2033 }, // compared alongside CWD_NOT_FOUND (1803); no message
  TOKEN_DECRYPT_FAILED: { code: 2140 }, // thrown with no message
  USER_KEY_ENCRYPT_FAILED: { code: 2141 }, // thrown with no message
  LEGACY_SYMMETRIC_DECRYPT_FAILED: { code: 2150 }, // thrown with no message
  WEBCRYPTO_IMPORT_KEY_FAILED: { code: 2200, message: 'Import key failed for WebCrypto' },

  CRYPT_BUFFER_ENCRYPT_FAILED: { code: 2501, message: 'Encryption failed' }, // AES encyryption failed crypt-buffer
  CRYPT_BUFFER_DECRYPT_FAILED: { code: 2502, message: 'Decryption failed' }, // AES decryption failed crypt-buffer
  CRYPT_NATIVE_ENCRYPT_FAILED: { code: 2503, message: 'Encryption failed' }, // AES encryption failed crypt-native
  CRYPT_NATIVE_DECRYPT_FAILED: { code: 2504, message: 'Decryption failed' }, // AES decryption failed crypt-native
  CRYPT_NATIVE_KEY_STRETCH_FAILED: { code: 2505, message: 'Key stretch failed' }, // PBKDF2 failed crypt-native

  // error codes 3000 -> 3200 are related to private downloads
  // download errors
  BROWSER_UNSUPPORTED_DECRYPT: { code: 3000, message: 'Your web browser is not fully supported.' }, // Unable to decrypt file in browser
  BROWSER_UNSUPPORTED_DOWNLOAD: { code: 3010, message: 'Your web browser is not fully supported.' }, // Your browser is unable to download this file.
  BROWSER_UNSUPPORTED_SAVE: { code: 3011, message: 'Your web browser is not fully supported.' }, // Your browser is unable to save files.
  BROWSER_UNSUPPORTED_XHR2: { code: 3012, message: 'Your web browser is not fully supported.' }, // Your browser does not support xhr2
  FILE_TOO_LARGE_BROWSER_DECRYPT: { code: 3020, message: 'File too large to download with browser decryption.' }, // The file is too large to be downloaded in this browser.  Please use Google Chrome
  DISK_SPACE_INSUFFICIENT: { code: 3021, message: 'Not enough space on disk to download this file.' },

  // storage filesystem
  BROWSER_PRIVATE_MODE: { code: 3050, message: 'Your web browser encountered an error.' }, // Private browsing or Ingcognito may be enabled which prevents us from downloading the data.
  BROWSER_DISK_SPACE: { code: 3100, message: 'Your web browser encountered an error.' }, // You might be out of disk space.  Please retry after clearing some space
  BROWSER_FILE_WRITE_FAILED: { code: 3101, message: 'Your web browser encountered an error.' }, // Could not write the file, file writer exception
  BROWSER_STORAGE_DENIED: { code: 3110, message: 'Your web browser encountered an error.' }, // We were unable to store data on your browser.  Please ensure you have allowed Sync.com to store data on your computer.
  BROWSER_FILE_ENTRY_MISSING: { code: 3111, message: 'Your web browser encountered an error.' }, // Unable to retrieve a file entry
  BROWSER_FILE_WRITER_CREATE_FAILED: { code: 3112, message: 'Your web browser encountered an error.' }, // Error creating a file writer

  // previews
  PREVIEW_SESSION_EXPIRED: {
    code: 3200,
    message: 'Sorry, your session has expired. Please refresh the page to continue.',
  }, // Preview encountered an error
  PREVIEW_NETWORK_ERROR: {
    code: 3201,
    message: 'Sorry, your session has expired. Please refresh the page to continue.',
  }, // Preview encountered a network error
  PREVIEW_UNKNOWN_ERROR: {
    code: 3202,
    message: 'Sorry, your session has expired. Please refresh the page to continue.',
  }, // Preview encountered an unknown error
  PREVIEW_CLIENT_MISMATCH: { code: 3210, message: 'This request was not initiated by the same client' }, // IP address incorrect
  PREVIEW_REQUEST_EXPIRED: { code: 3211, message: 'This request has expired, please try again.' }, // Server incorrect
  PREVIEW_NOT_ALLOWED_ACCOUNT: { code: 3212, message: 'Preview is not allowed on this account' },
  DOCUMENT_PASSWORD_INCORRECT: { code: 3213, message: 'Document password is incorrect' },

  INCOMPATIBLE_CHARACTERS_OR_FILETYPES: {
    code: 3500,
    message: `Incompatible Characters or Filetypes detected. Please see <a href='${HELP_LINKS.WHY_FILES_NOT_SYNCING}' target='_blank'>this page</a>.`,
  },
  COPY_MOVE_SAME_LOCATION: { code: 3610, message: 'You cannot copy/move the file to the same location.' },

  BROWSER_NO_JSON: { code: 4001, message: 'The browser does not support JSON.' },
  BROWSER_NO_ADD_EVENT_LISTENER: {
    code: 4002,
    message: 'This browser is very old and cannot support addEventListener',
  },
  LOCAL_STORAGE_FAILED: { code: 4101, message: 'Local storage was unsuccessful' },
  LOCAL_STORAGE_PRIVATE_MODE: {
    code: 4102,
    message:
      'Local storage was unsuccessful.  Private browsing mode may be enabled.  Please disable private browsing mode',
  },
  BROWSER_DOWNLOAD_DECRYPT_UNSUPPORTED: {
    code: 5001,
    message: 'Your browser is unable to download and decrypt data.  We suggest upgrading to the latest version.',
  },

  LOGIN_CREDENTIALS_INCORRECT: { code: 6001, message: 'Incorrect login credentials' },
  LOGIN_ERROR: { code: 6002, message: 'Error logging in, please contact support' },
  PLAN_FETCH_ERROR: { code: 6003, message: 'Error in fetching plan' },
  PLAN_INVALID: { code: 6004, message: 'Invalid plan selected' },
  CHARGEBEE_SUBSCRIPTION_ID_EMPTY: { code: 6005, message: 'Chargebee subscription ID is empty' },
  SUBSCRIPTION_DETAILS_LOAD_ERROR: { code: 6006, message: 'Error loading subscription details' },
  COUPON_INVALID: { code: 6007, message: 'Invalid coupon' },
  NO_UNPAID_INVOICES: { code: 6008, message: 'No unpaid invoices found.' },
  EMAIL_TOO_LONG: { code: 6009, message: 'Email cannot exceed 70 characters.' },
  PLAN_NOT_ELIGIBLE_DOWNGRADE: { code: 6010, message: 'Plan not eligible for downgrade.' },
  AUTO_COLLECTION_ENABLE_FAILED: { code: 6011, message: 'Failed to enable auto collection. Please try again later.' },
  // Chargebee refused the payment; carries the gateway's own wording.
  CHARGEBEE_PAYMENT_FAILED: { code: 6012 },
  // A plan change timed out after it was sent, so it may have applied.
  CHARGEBEE_CHANGE_UNCONFIRMED: { code: 6014 },
  // A restricted domain tried to sign up for a plan; same wording as a blocked free signup.
  RESTRICTED_DOMAIN_SIGNUP: { code: 6015, message: 'Email already taken.' },

  UPLOAD_CONNECTION_ERROR: { code: 7000, message: 'Connection error uploading' },
  DOWNLOAD_INVALID_RESPONSE: { code: 7001, message: 'Received invalid response attempting to download this file' },
  UPLOAD_TIMEOUT: { code: 7005, message: 'Upload timed out.' },
  UPLOAD_SOURCE_READ_ERROR: { code: 7010, message: 'Error reading source file for upload' },
  UPLOAD_PAYLOAD_SIZE_MISMATCH: { code: 7011, message: 'Uploaded payload does not match expected size' },
  UPLOAD_FILE_CHANGED: { code: 7012, message: 'Uploaded file has changed, please re-queue' },
  UPLOAD_OUT_OF_MEMORY: {
    code: 7013,
    message: 'Not enough memory to upload this file. Reload the page and try again.',
  },
  UPLOAD_INVALID_RESPONSE: { code: 7020, message: 'Invalid response received during file upload' },
  UPLOADS_NOT_ENABLED: { code: 7021, message: 'Uploads are not enabled' },
  BACKUP_FINISH_INVALID_RESPONSE: { code: 7022, message: 'Invalid response received while finishing the backup' },
  UPLOAD_NO_RESPONSE: { code: 7023, message: 'No response recieved during upload' },
  BACKUP_FINISH_NO_SUCCESS: { code: 7024, message: 'Error finishing the backup, did not receive success' },
  FILE_ALREADY_EXISTS: { code: 7030, message: 'File already exists.  Rename and try again.' },
  DOWNLOAD_CONNECTION_ERROR: { code: 7100, message: 'Connection error downloading' },
  DOWNLOAD_TIMEOUT: { code: 7101, message: 'Timeout error downloading' },
  FILE_UNAVAILABLE: { code: 7102, message: 'The file is unavailable' }, // 404 from mfs
  FILE_CORRUPTED: { code: 7103, message: 'This file may be corrupted or damaged' },
  DOWNLOAD_CANCELLED: { code: 7104, message: 'Download was cancelled' },
  FOLDER_UPLOAD_NOT_SUPPORTED: { code: 7200, message: 'Uploading folders are not supported' },
  FILE_NAME_UNDETERMINED: { code: 7201, message: 'Cannot determine file name' },
  EMPTY_FILE_UPLOAD: { code: 7202, message: 'Empty file (0kb) cannot be uploaded' },
  FOLDER_UPLOAD_CANCELLED: { code: 7210, message: 'Cancelled upload of folder' },
  DIRECTORY_READ_ERROR: { code: 7211, message: 'Error occurred reading directory' },
  DIRECTORY_CREATE_ERROR: { code: 7212, message: 'Error creating a directory' },
  UPLOAD_LOCATION_UNDETERMINED: {
    code: 7213,
    message: 'Could not determine location to upload files.  Please refresh the page and try again.',
  },
  FOLDER_UPLOAD_UNKNOWN_ERROR: { code: 7214, message: 'An unknown error occurred with folder upload' },
  FILE_UPLOAD_PREPARE_ERROR: { code: 7215, message: 'Error preparing file to upload' },
  FILE_UPLOAD_RETRIEVE_ERROR: { code: 7216, message: 'Could not retrieve the file to upload' },
  FOLDER_UPLOAD_TOO_LARGE_USE_DESKTOP: {
    code: 7240,
    message: 'For large folder uploads you will need to upload via the Sync desktop app',
  },
  FILE_NAME_REQUIRED: { code: 7241, message: 'Please enter a name for this file' },
  FILE_TYPE_REQUIRED: { code: 7242, message: 'Please select the type of file' },
  FILE_NAME_CONTAINS_SLASHES: { code: 7243, message: 'Filenames must not contain slashes (/ or \\)' },
  DOCUMENT_CREATE_ERROR: { code: 7244, message: 'Error in create document' },
  FOLDER_NAME_EXISTS: { code: 7245, message: 'A folder with this name already exists at this location.' },
  FOLDER_NAME_INVALID_CHARACTERS: {
    code: 7246,
    message: `Folder name cannot contain the following characters: / \\`,
  },
  FOLDER_CREATE_ERROR: { code: 7247, message: `An error occurred` },

  // Zip error codes
  ZIP_PREPARE_FATAL_ERROR: { code: 7500, message: 'Fatal error preparing zip file.' },
  ZIP_DOWNLOAD_EMPTY_ERROR: { code: 7501, message: 'Empty folders cannot be zipped' },
  ZIP_SERVER_NO_SPACE: { code: 7502, message: 'Server error: Not enough space' },
  ZIP_SERVER_UNKNOWN_STATUS: { code: 7503, message: 'Server error: Unknown status code' },

  // E2EE zip error codes (7511-7515)
  ZIP_DOWNLOAD_CREATE_ERROR: { code: 7511, message: 'Failed to create zip archive' },
  ZIP_STREAMING_UNSUPPORTED: { code: 7512, message: 'Streaming download is not supported in this browser' },
  ZIP_DOWNLOAD_CANCELLED_ERROR: { code: 7513, message: 'Zip download was cancelled' },
  ZIP_DOWNLOAD_SIZE_CAPPED_ERROR: {
    code: 7514,
    message: `This download is too large for your browser. Please try using Chrome or select fewer files. <a href="${HELP_LINKS.ZIP_DOWNLOADS}" target="_blank" rel="noopener noreferrer">More info</a>`,
  },
  ZIP_DOWNLOAD_FOLDER_ENUMERATION_ERROR: { code: 7515, message: 'Some folders couldn’t be read and were skipped.' },

  // errors from server Authorization
  ACCOUNT_MUST_BE_PRO: { code: 8000, message: 'You must have a pro account' },
  EMAIL_VERIFICATION_REQUIRED: { code: 8001, message: 'Your email must be verified in order to use this feature' },
  FOLDER_READ_ONLY: { code: 8002, message: 'This is a read-only folder' },
  DISK_SPACE_LIMIT_REACHED: { code: 8003, message: 'Disk space limit reached' },
  IP_RATE_LIMITED: { code: 8004, message: 'Your ip address has been rate-limited for 30 minutes' },
  SHARE_ADMIN_REQUIRED: { code: 8005, message: 'You must be the share admin to use this feature' },
  EMAIL_RATE_LIMITED: { code: 8006, message: 'Your account has been rate-limited for sending out too many emails' },
  CHILD_ACCOUNT_FEATURE_UNAVAILABLE: { code: 8007, message: 'As a child account, this feature is unavailable.' },
  MULTI_USER_ADMIN_REQUIRED: { code: 8008, message: 'You must be the admin of your Multi User account' },
  EMAIL_CHANGE_LIMIT_REACHED: {
    code: 8009,
    message: `You've hit the email change limit. We're ensuring security. Please wait a bit before trying again. Thanks!`,
  },
  LINK_PASSWORD_REQUIRED: { code: 8018, message: 'Please enter the link password.' },
  LINK_PASSWORD_INCORRECT: { code: 8019, message: 'The link password is incorrect' },
  LINK_EXPIRED: { code: 8020, message: 'This link has expired, please contact the owner of the link' },
  LINK_DOWNLOAD_LIMIT_REACHED: {
    code: 8021,
    message: 'This link has reached the download limit.  Please contact the owner of the link',
  },
  AUTH_TOKEN_REQUIRED: { code: 8022, message: 'Please enter your Authentication Token' },
  AUTH_2FA_INCORRECT: { code: 8023, message: 'Authentication credentials are incorrect' }, // handled interally in API - 2fa incorrect
  AUTH_CREDENTIALS_INCORRECT: { code: 8024, message: 'Authentication credentials are incorrect' },
  LINK_SUSPENDED: { code: 8025, message: 'This link has been suspended' },
  LINK_DELETED_BY_OWNER_ALT: { code: 8026, message: 'This link has been deleted by the owner.' },
  PREVIEW_NOT_ALLOWED: { code: 8027, message: 'Preview not allowed.' },
  LINK_LIMIT_REACHED: { code: 8028 }, // compared only; message provided by the API

  EMAIL_VERIFY_FOR_SHARING: { code: 8030, message: 'Please verify your email address to enable sharing features.' },
  LINK_DAILY_DOWNLOAD_LIMIT_REACHED: {
    code: 8031,
    message: 'Link has reached daily download limit. Contact link owner or upgrade account to get unlimited downloads.',
  },

  ACCOUNT_CANCELLED: { code: 8050, message: 'This account is cancelled' },
  ACCOUNT_SUSPENDED: { code: 8051, message: 'Account suspended.' },
  LINK_UNAVAILABLE: { code: 8052, message: 'This link is currently unavailable, please contact the link owner' },
  ACCOUNT_CLOSED: { code: 8053, message: 'This account has been closed' },
  ACCOUNT_MIGRATING: { code: 8055, message: 'Account migrating' },
  ACCOUNT_MIGRATED: { code: 8056, message: 'Account migrated' },
  ACCOUNT_MIGRATING_ALT: { code: 8057, message: 'Account migrating' },

  SESSIONNEW_CREDENTIALS_INCORRECT_1: { code: 8060, message: 'Authentication credentials are incorrect' }, // SessionNew wrong credentials multiple times
  SESSIONNEW_CREDENTIALS_INCORRECT_2: { code: 8061, message: 'Authentication credentials are incorrect' }, // SessionNew wrong credentials multiple times

  EMAIL_ALREADY_YOURS: { code: 8100, message: 'This is already your email address' },
  PASSWORD_INCORRECT: { code: 8101, message: 'Incorrect password' },
  EMAIL_IN_USE: { code: 8102, message: 'This email is already in use' },
  VERIFY_CURRENT_EMAIL: { code: 8103, message: 'Please verify your current email' },
  EMAIL_CHANGE_REQUEST_INVALID: { code: 8104, message: 'This email change request is invalid or already used' },
  EMAIL_CHANGE_REQUEST_EXPIRED: { code: 8105, message: 'This email change request has expired' },
  EMAIL_TAKEN: { code: 8106, message: 'This email is taken, please choose a new one' },
  EMAIL_INVALID_FORMAT: {
    code: 8107,
    message: 'This email is an invalid format.  Please contact support if this is a mistake',
  },
  SSO_CREDENTIALS_INCORRECT: { code: 8108, message: 'The single sign-on authentication credentials are incorrect' },
  SSO_NOT_AVAILABLE: { code: 8109, message: 'Single sign-on is not available on this account' },
  SSO_NOT_AVAILABLE_ALT: { code: 8110, message: 'Single sign-on is not available on this account' }, // acct does not exist
  EMAILS_INVALID_FORMAT: { code: 8111, message: 'One or more emails are in invalid format.' },
  FILE_READ_ERROR: { code: 8112, message: 'Error is occured while reading file.' },
  CSV_FILE_INVALID: { code: 8113, message: 'Please import valid .csv file.' },
  CSV_OR_EMAILS_REQUIRED: { code: 8114, message: 'Please select a csv file or add user email(s) in textbox.' },
  EMAILS_MISSING_ROLE: { code: 8115, message: "One or more emails doesn't have a role defined." },
  EMAILS_LIMIT_EXCEEDED: { code: 8116, message: 'Number of emails to add exceeds allowed limit.' },
  CSV_FORMAT_INVALID: { code: 8117, message: 'The CSV file format is not valid.' },
  CSV_EMAILS_MISSING: { code: 8118, message: "One or more emails doesn't exist in CSV." },
  TEAM_USER_LIMIT_EXCEEDED: {
    code: 8119,
    message: 'Number of users to add exceeds the allowed user for current team.',
  },
  PASSWORD_CHANGE_ERROR: {
    code: 8120,
    message: 'An error occurred changing your password.  Your password remains the same',
  },
  PASSWORDS_DO_NOT_MATCH: { code: 8121, message: 'Your passwords do not match' },
  USERS_ALREADY_PROVISIONED: {
    code: 8122,
    message:
      'One or more users are already part of another team or are already in provisioning. The following users will not be added:',
  },
  USER_ALREADY_ASSIGNED_ROLE: {
    code: 8123,
    message: 'This user is already assigned to this role, please select a new one.',
  },
  ROLE_DOES_NOT_EXIST: { code: 8124, message: 'One or more roles does not exist on this account.' },
  PASSWORD_RESET_NOT_ENABLED: { code: 8130, message: 'Password reset was not enabled on this account' },
  PASSWORD_RESET_SEND_ERROR: {
    code: 8131,
    message: 'An error occurred sending the password reset.  Please try again in a few minutes',
  },
  PASSWORD_RESET_KEYS_ENCRYPT_ERROR: { code: 8132, message: 'An error occurred encrypting reset keys' },
  PASSWORD_RESET_KEYS_DECRYPT_ERROR: { code: 8133, message: 'An error occurred decrypting reset keys' },
  PASSWORD_RESET_PRIVATE_KEY_ERROR: { code: 8134, message: 'An error occurred with the password reset private key' },
  PASSWORD_RESET_ENABLE_ERROR: { code: 8135, message: 'An error occurred enabling password reset' },
  PASSWORD_RESET_COMPLETE_ERROR: { code: 8136, message: 'An error occurred completing the password reset request' },
  INVITATION_EXPIRED: { code: 8137, message: 'The invitation has expired, please contact your admin.' },
  WEBAUTH_ACCOUNT_NOT_EXIST_ERROR: { code: 8138, message: 'Email does not exist' },
  WEBAUTH_INVALID_REDIRECT_URI_ERROR: { code: 8139, message: 'The redirect_uri provided is invalid' },
  WEBAUTH_SESSION_EXPIRED_ERROR: { code: 8140, message: 'Sign-in session expired' },

  MULTI_USER_PLAN_LIMIT_EXCEEDED: { code: 8200, message: 'This would exceed your limit for your multi user plan' },
  USER_NOT_FOUND: { code: 8201, message: 'Cannot locate this user' },
  USER_NOT_CHILD_OF_PLAN: {
    code: 8202,
    message: 'This user does not exist, or is not a child of your multi user plan',
  },
  REFERRAL_CODE_INCORRECT: { code: 8203, message: 'The referral code is incorrect' },
  TEAMS_ONLY_FREE_ACCOUNTS: { code: 8205, message: 'You may only add free accounts to your Teams plan.' }, // This error appears when legacy multiadmin user tries to add paid user.
  USER_ALREADY_UNDER_PLAN: { code: 8206, message: 'This user is already under your multi user plan' },
  PARENT_SUBSCRIPTION_INVALID: {
    code: 8207,
    message: `Parent user does not have a valid subscription, please contact support at ${HELP_LINKS.SUPPORT}`,
  },
  MAX_USERS_LIMIT: {
    code: 8210,
    message: 'We currently allow a maximum of 99 users.  Please contact us if you require more.',
  },

  FILE_NAME_EXISTS: { code: 8211, message: 'A file exists with this name already' },
  FILE_EXTENSION_INVALID: { code: 8212, message: 'The file extension must be (.docx, .pptx, .xlsx)' },
  FILE_NAME_INVALID: { code: 8213, message: 'File name is not valid' },

  // related to multi user
  ADD_USER_INVALID_ACTION: { code: 8220, message: 'Invalid action for adding a new user' },
  ACTION_DOES_NOT_EXIST: { code: 8221, message: 'Action does not exist' },

  TERMS_ACCEPTANCE_REQUIRED: {
    code: 8300,
    message: 'You must accept the terms of service and privacy policy to sign up',
  },
  PASSWORDS_DO_NOT_MATCH_SIGNUP: { code: 8301, message: 'Your passwords do not match' },
  ACCOUNT_ALREADY_SIGNED_UP: {
    code: 8302,
    message: 'This account has already signed up, please log in to join this share',
  },
  PASSWORD_MISSING: { code: 8303, message: 'Missing password' },
  PASSWORD_LENGTH_INVALID: {
    code: 8304,
    message: 'Password should be minimum of 8 characters and maximum of 72 characters',
  },
  PASSWORDS_MISSING: { code: 8305, message: 'Missing one or more passwords' },

  BILLING_PROFILE_LOAD_ERROR: { code: 8500, message: 'An error occurred loading your billing profile' }, // iframe error
  BILLING_SETTINGS_LOAD_ERROR: { code: 8501, message: 'An error occurred loading your billing settings' }, // dialog autorenew
  LEGACY_ACCOUNT_CONTACT_BILLING: {
    code: 8505,
    message: 'Legacy Account: Please contact billing@sync.com if you wish to make changes to your plan',
  },
  CONTACT_BILLING_UPGRADE: { code: 8506, message: 'Please contact billing to complete the upgrade' },
  PRODUCTS_LOAD_ERROR: { code: 8520, message: 'An error occurred loading the products' },
  ORDER_SESSION_CREATE_ERROR: { code: 8550, message: 'An error occurred creating the order session' },
  SUBSCRIPTION_CANCELLED_STATE: {
    code: 8551,
    message: `Your subscription is in a cancelled state.  Please contact support at ${HELP_LINKS.SUPPORT} for assistance`,
  },

  PERMISSION_DENIED: { code: 8401, message: 'Permission denied' },

  // The first three carry no message: CP re-arms and retries, so they are never shown.
  CHECKOUT_TOKEN_MISSING: { code: 8990 },
  CHECKOUT_TOKEN_INVALID: { code: 8991 },
  CHECKOUT_TOKEN_EXHAUSTED: { code: 8992 },

  BOT_PROTECTION_BLOCKED_BY_CLOUDFLARE: { code: 8998, message: 'You are blocked by Cloudflare.' },
  BOT_PROTECTION_BLOCKED_TRAFFIC: { code: 8999, message: 'Blocked Traffic' },

  API_TALK_ERROR: { code: 9000, message: 'An error occurred talking to the API' },
  API_EMPTY_RESULT: { code: 9001, message: 'An empty result was returned from the API' },
  TURNSTILE_ERROR: { code: 9002 }, // constructed with a custom message

  // These are HTTP STATUS code errors.  CODE - 9000 = HTTP status.  E.g., 9429 = http status 429
  NOT_AUTHORIZED: { code: 9400, message: 'Not authorized' },
  NOT_LOGGED_IN: { code: 9401, message: 'You are not logged in' },
  SESSION_EXPIRED: { code: 9402, message: 'Your session has expired' },
  RESOURCE_ACCESS_ERROR: { code: 9403 }, // constructed with a custom message
  TOO_MANY_SIGNIN_ATTEMPTS: {
    code: 9429,
    message: `Too many failed sign in attempts. Wait 30 minutes before trying again. <a target='_blank' href="${HELP_LINKS.LOGIN_ERROR_9429}">Learn more</a>.`,
  },
  SESSIONNEW_CREDENTIALS_INCORRECT_3: { code: 9451, message: 'Authentication credentials are incorrect' }, // SessionNew wrong credentials multiple times
  SESSIONNEW_CREDENTIALS_INCORRECT_4: { code: 9452, message: 'Authentication credentials are incorrect' }, // SessionNew wrong credentials multiple times
  REGION_NOT_AUTHORIZED: { code: 9453, message: 'Not authorized region' }, // User's region didn't match with server region
  INTERNAL_SERVER_ERROR: {
    code: 9500,
    message: `Internal server error. Visit <a href='${HELP_LINKS.SUPPORT}' target='_blank'>${HELP_LINKS.SUPPORT}</a>`,
  },
  TIMEOUT_ERROR: { code: 9502, message: 'Timeout error' },
  GATEWAY_TIMEOUT_ERROR: { code: 9504, message: 'Gateway timeout error' },
  SERVERS_BUSY_1: { code: 9521, message: 'Our servers are a bit busy. Please try again shortly.' },
  SERVERS_BUSY_2: { code: 9522, message: 'Our servers are a bit busy. Please try again shortly.' },
  SERVERS_BUSY_3: { code: 9523, message: 'Our servers are a bit busy. Please try again shortly.' },
  SERVERS_BUSY_4: { code: 9524, message: 'Our servers are a bit busy. Please try again shortly.' },
  SERVERS_BUSY_5: { code: 9525, message: 'Our servers are a bit busy. Please try again shortly.' },
  SERVERS_BUSY_6: { code: 9526, message: 'Our servers are a bit busy. Please try again shortly.' },
  SERVERS_BUSY_7: { code: 9527, message: 'Our servers are a bit busy. Please try again shortly.' },
  SERVERS_BUSY_8: { code: 9528, message: 'Our servers are a bit busy. Please try again shortly.' },
  SERVERS_BUSY_9: { code: 9529, message: 'Our servers are a bit busy. Please try again shortly.' },
  NO_INTERNET: { code: 9999, message: 'No internet connection. Please check your network and try again.' },

  // Office Errors
  OFFICE_HANDSHAKE_ERROR: { code: 11000, message: 'An error occurred sending the file to Microsoft' }, // An unknown error occured during the handshake
  OFFICE_FILE_OUT_OF_DATE: {
    code: 11001,
    message: 'This file is too out of date to work on collaboratively; please wait or contact support.',
  },
  OFFICE_INIT_DATA_ERROR: { code: 11002, message: 'An error occurred sending the file to Microsoft' }, // Could not create the initialization data.
  OFFICE_WOPI_COMM_ERROR: { code: 11003, message: 'An error occurred sending the file to Microsoft' }, // Could not communicate with WOPI.
  OFFICE_HANDSHAKE_START_ERROR: {
    code: 11004,
    message: 'The handshake could not be started to initialize communication with Microsoft',
  }, // Got a bad response from WOPI.
  OFFICE_IFRAME_POST_ERROR: { code: 11005, message: 'An error occurred sending the file to Microsoft' }, // Could not Post to iFrame.
  OFFICE_REQUEST_TIMEOUT: {
    code: 11006,
    message: 'An error occurred sending the file to Microsoft, request took too long',
  },
  OFFICE_OPERATION_NOT_ALLOWED: { code: 11009, message: 'This operation is not allowed' }, // Not running with fullscreen edit, fullscreen view, or embedded view
  OFFICE_FILE_INCOMPATIBLE: { code: 11010, message: 'This file is not compatible with Office 365' }, // An illegal operation occured.

  // OAuth Errors
  OAUTH_UNKNOWN_ERROR: { code: 11100, message: 'An unknown error occured during authentication' }, // General error for OAuth, don't use if we know the details
  OAUTH_LINK_INVALID: { code: 11101, message: 'This link is not valid' }, // Fields are missing on the link, or nonce doesn't exist
  OAUTH_LINK_EXPIRED: { code: 11102, message: 'This link has expired' }, // The nonce has expired.
  OAUTH_APP_LINK_NOT_ALLOWED: { code: 11103, message: 'You cannot link with this app' }, // ?
  OAUTH_APP_ALREADY_LINKED: {
    code: 11104,
    message: 'You already have this app linked, remove it before attempting to link again',
  },

  // Apps/App Share Errors
  APP_CONNECT_ERROR: { code: 11200, message: 'An error occured connecting apps to this folder' }, // Catch-all for app specific issues
  APP_SHARE_ERROR: { code: 11201, message: 'An error occured sharing this folder with the app' }, // Catch-all for share specific issues
  APP_WEBPATH_ERROR: { code: 11202, message: 'An error occured with the folder connecting to the app' }, // Catch-all for webpath specific issues
  APP_CONNECT_PERMISSION_DENIED: {
    code: 11203,
    message: 'You do not have permission to connect apps to this share; contact the share owner',
  },
  APP_FEATURE_UNAVAILABLE: { code: 11210, message: 'This feature is not available for your account at this moment' },
  FOLDER_TOO_LARGE_TO_DISPLAY: { code: 11211, message: 'This folder is too large too display' }, // Sharepathlist exceeds hard_limit

  // ZIP server errors are extracted from API result
  ZIP_SERVER_CMD_CREATE_FAILED: { code: 151000, message: 'An error occurred creating zip' }, // # 1000: "Creating cmd zipscript.exe failed"
  ZIP_SERVER_CMD_INVALID: { code: 151001, message: 'An error occurred creating zip' }, // # 1001: "CMD to run zipscript.exe is invalid"
  ZIP_SERVER_PROCESS_OPEN_ERROR: { code: 151002, message: 'An error occurred creating zip' }, // # 1002: "process zipscript.exe open error"
  ZIP_SERVER_THREAD_CREATE_ERROR: { code: 151003, message: 'An error occurred creating zip' }, // # 1003: "error creating thread "
  ZIP_SERVER_FILE_CREATE_FAILED: { code: 151005, message: 'An error occurred creating zip' }, // # 1005: "Zip file create failed."
  ZIP_SERVER_INVALID_REPORT_REQUEST: { code: 151006, message: 'An error occurred creating zip' }, // # 1006: "Invalid zip report request."
  ZIP_SERVER_INDEX_FILE_FAILED: { code: 151007, message: 'An error occurred creating zip' }, // # 1007: "Creating index file failed."
  ZIP_SERVER_INVALID_INPUT: { code: 151008, message: 'An error occurred creating zip' }, // # 1008: "invalid input or invalid data received from server"
  ZIP_SERVER_FILENAME_DECRYPT_FAILED: { code: 151009, message: 'An error occurred creating zip' }, // # 1009: "decrypting file name failed. "
  ZIP_SERVER_DELETE_CHANNEL_INVALID_INPUT: { code: 151010, message: 'An error occurred creating zip' }, // # 1010: "Invalid Input for curl XDELETE to delete channel"
  ZIP_SERVER_INVALID_RESPONSE: { code: 151011, message: 'An error occurred creating zip' }, // # 1011: "Got invalid response from server"
  ZIP_SERVER_FREE_SPACE_ERROR: { code: 151012, message: 'An error occurred creating zip' }, // # 1012: "calculating free space error."
  ZIP_SERVER_FOLDER_TOO_LARGE: { code: 151013, message: 'This folder is too large to create a zip file.' }, // # 1013: "Folder too large to zip (NOT SUPPORT)"
} as const satisfies Record<string, ApiErrorDef>;

export const ERROR_CODES = Object.fromEntries(
  Object.entries(ERROR_DEFS).map(([name, def]) => [name, (def as ApiErrorDef).code]),
) as { readonly [K in keyof typeof ERROR_DEFS]: number };

// eslint-disable-next-line @typescript-eslint/naming-convention
export const ErrorMessages: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(
    (Object.values(ERROR_DEFS) as ApiErrorDef[])
      .filter((def) => def.message !== undefined)
      .map((def) => [String(def.code), def.message as string]),
  ),
);
